import { useEffect } from 'react';

import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import { usePriceList } from '@zenbusiness/application-commons-pricelist';

import CalloutAsset from '#client/components/CalloutAsset';
import { formatOptionsForCart } from '#client/components/MultipleJurisdictionsSelection';
import PageHeader from '#client/components/PageHeader';
import SingleChoiceSelection from '#client/components/SingleChoiceSelection';
import useFlowState from '#client/hooks/flow/useFlowState';
import useAddToCart from '#client/hooks/useAddToCart';
import useFlowNavigation from '#client/hooks/useFlowNavigation';
import useRemoveFromCart from '#client/hooks/useRemoveFromCart';
import TwoColumnLayout from '#client/layouts/TwoColumnLayout';

const PRODUCT_NAME = 'change_of_registered_agent';

export const OPTIONS = {
  yes: 'yes',
  no: 'no'
};

const ChangeOfRegisteredAgent = () => {
  const { formatMessage } = useIntl();
  const { goToNextPage } = useFlowNavigation();
  const { businessInfo, set } = useFlowState();
  const {
    addedToCart,
    addToCart,
    inProgress: inProgressAddToCart
  } = useAddToCart({ protectedServices: [PRODUCT_NAME] });
  const {
    removedFromCart,
    removeFromCart,
    inProgress: inProgressRemoveFromCart
  } = useRemoveFromCart();
  const { loading: priceListLoading, get: getProduct, priceListUuid } = usePriceList();
  const product = getProduct(PRODUCT_NAME);

  if (!priceListLoading && !product) {
    throw new Error(`Could not find ${PRODUCT_NAME} in price list ${priceListUuid}`);
  }

  const { servicePricingUuid } = product.pricing;
  const { jurisdictionState, cora, previousJurisdictionState } = businessInfo;

  const selectedJurs = cora?.jurisdictions || [];

  useEffect(() => {
    if (addedToCart || removedFromCart) {
      goToNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addedToCart, removedFromCart]);

  const handleOnClick = async (option = null) => {
    set('businessInfo.changeOfRegisteredAgent', option);

    if (previousJurisdictionState) {
      await removeFromCart({
        servicePricingUuid,
        jurisdictionState: previousJurisdictionState
      });
    }

    if (option === 'yes') {
      if (selectedJurs?.length > 0) {
        const itemsToAdd = formatOptionsForCart(selectedJurs, servicePricingUuid, PRODUCT_NAME);
        await addToCart(itemsToAdd);
      }
    } else {
      const itemsToRemove = [
        {
          servicePricingUuid,
          jurisdictionState
        }
      ];

      if (selectedJurs?.length > 0) {
        selectedJurs.forEach((element) => {
          itemsToRemove.push({ servicePricingUuid, jurisdictionState: element.value });
        });
      }
      await removeFromCart(itemsToRemove);
    }
  };

  const choices = Object.entries(OPTIONS).map(([key, value]) => ({
    value,
    label: formatMessage({
      id: `shopping.page.changeOfRegisteredAgent.option.${key}.label`
    })
  }));

  return (
    <TwoColumnLayout>
      <TwoColumnLayout.SmallColumn>
        <PageHeader
          subtitle={<FormattedMessage id="shopping.page.changeOfRegisteredAgent.subtitle" />}
          title={<FormattedMessage id="shopping.page.changeOfRegisteredAgent.title" />}
        />
        <SingleChoiceSelection
          defaultValue={OPTIONS.yes}
          options={choices}
          primaryAction={{
            label: formatMessage({
              id: 'shopping.page.changeOfRegisteredAgent.primaryAction.label'
            }),
            loading: inProgressAddToCart || inProgressRemoveFromCart,
            clickstreamProps: { elementName: 'changeOfRegisteredAgent_primary_action' },
            'data-testid': 'change-of-registered-agent-primary-action',
            onClick: handleOnClick
          }}
          value={businessInfo.changeOfRegisteredAgent}
        />
      </TwoColumnLayout.SmallColumn>
      <TwoColumnLayout.LargeColumn>
        <CalloutAsset name="browsing-computer" />
      </TwoColumnLayout.LargeColumn>
    </TwoColumnLayout>
  );
};

export default ChangeOfRegisteredAgent;
